import { ErrorModal, useContextMenuMESF } from "@dexteel/mesf-core";
import { Grid2, Paper, Typography } from "@mui/material";
import {
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useState } from "react";

import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { FooterButton } from "@/controls/buttons/FooterButton";
import { CenteredLazyLoading } from "@/controls/LazyLoading";

import { TextFieldSearchFilter } from "@/shared/components/TextFieldSearchFilter";
import { useNavigate } from "react-router-dom";
import { CatalogAdjustmentType } from "../../../models/CatalogAdjustmentType";
import { typeUrl, useAdjustmentTypes } from "../CatalogAdjustmentTypePage";
import { useAdjustmentTypeOptionsFunctions } from "../hooks/useAdjustmentTypeOptionsFunctions";
import { useTableData } from "../hooks/useTableData";
import { UpsertDeleteModal } from "./UpsertDeleteModal";

export const TableAdjustmentType = ({
	typeId,
}: {
	typeId: string | undefined;
}) => {
	const navigate = useNavigate();

	const [filterValue, setFilterValue] = useState("");
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [showModal, setShowModal] = useState<
		"create" | "update" | "delete" | ""
	>("");
	const [selectedRowData, setSelectedRowData] =
		useState<CatalogAdjustmentType | null>(null);
	const [error, setError] = useState("");

	const { data: rows, isLoading } = useAdjustmentTypes((error) =>
		setError(error.message),
	);

	const handleNewType = () => {
		if (gridApi) gridApi.deselectAll();
		setShowModal("create");
		setSelectedRowData(null);
		navigate(`${typeUrl}/new`);
	};

	const editAdjustmentType = (data: CatalogAdjustmentType) => {
		setSelectedRowData(data);
		setShowModal("update");
		navigate(`${typeUrl}/${data.AdjustmentTypeId}`);
	};

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { getMenuOptions } = useAdjustmentTypeOptionsFunctions({
		setSelectedRowData,
		setShowModal,
		handleNewType,
		editAdjustmentType,
	});
	const { columnDefs } = useTableData({
		setSelectedRowData,
		setShowModal,
		showContextMenu,
	});

	const { defaultColDef } = useGridDefinitions({
		OnEdit: (data: CatalogAdjustmentType) => {
			setShowModal("update");
			setSelectedRowData(data);
		},
	});

	const onRowDoubleClicked = (event: any) => {
		const cell = gridApi?.getFocusedCell();
		if (cell?.column.getColId() !== "-" && event.node.isSelected()) {
			setSelectedRowData(event.data as CatalogAdjustmentType);
			setShowModal("update");
			navigate(
				`${typeUrl}/${(event.data as CatalogAdjustmentType).AdjustmentTypeId}`,
			);
		}
	};

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data = params.node?.data;
			showContextMenu(event as any, data, "adjustmentTypeContext");
			if (data) {
				params.api.deselectAll();
				params.node?.setSelected(true);
			}
			return [];
		},
		[rows],
	);

	const onModalHide = (id?: number) => {
		if (gridApi) gridApi.deselectAll();
		setShowModal("");
		navigate(typeUrl);
	};

	useEffect(() => {
		if (!isLoading) {
			if (typeId) {
				if (typeId === "new") {
					handleNewType();
				} else {
					const typeNode = gridApi?.getRowNode(typeId);

					if (typeNode!) {
						setFilterValue(
							(typeNode.data as CatalogAdjustmentType).AdjustmentTypeName,
						);
						gridApi?.setGridOption(
							"quickFilterText",
							(typeNode.data as CatalogAdjustmentType).AdjustmentTypeName,
						);

						gridApi?.ensureNodeVisible(typeNode);
						typeNode.setSelected(true);
						setSelectedRowData(typeNode.data);
						setShowModal("update");
					}
				}
			}
		}
	}, [isLoading, gridApi]);

	useEffect(() => {
		registerConfig({
			id: "adjustmentTypeContext",
			getOptions: getMenuOptions,
		});
	}, []);

	return (
		<Grid2 container justifyContent="center" p={1} spacing={1}>
			<Grid2 size={{ md: 12, xs: 12 }}>
				<Typography variant="h5" fontWeight={600}>
					Adjustment Types
				</Typography>
			</Grid2>
			<Grid2 size={{ xs: 12, md: 12 }}>
				<TextFieldSearchFilter
					value={filterValue}
					onChange={(val) => {
						setFilterValue(val);
						gridApi?.setGridOption("quickFilterText", val);
					}}
				/>
			</Grid2>
			<Grid2 container justifyContent="center" alignItems="center">
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Paper elevation={2} style={{ height: "64vh", width: "100%" }}>
						<AgGridReact<CatalogAdjustmentType>
							loading={isLoading}
							rowData={rows || []}
							columnDefs={columnDefs}
							defaultColDef={defaultColDef}
							rowHeight={34}
							headerHeight={34}
							animateRows
							getContextMenuItems={getContextMenuItems}
							onRowDoubleClicked={onRowDoubleClicked}
							onGridReady={(params) => setGridApi(params.api)}
							getRowId={(params) => `${params.data.AdjustmentTypeId}`}
							loadingOverlayComponent={CenteredLazyLoading}
							gridOptions={{
								rowSelection: {
									mode: "singleRow",
									checkboxes: false,
									enableClickSelection: true,
								},
							}}
						/>
					</Paper>
				</Grid2>
				<FooterButton
					label="ADJUSTMENT TYPE"
					showCreateModal={showModal === "create"}
					setShowCreateModal={(showCreateModal) =>
						showCreateModal ? handleNewType() : setShowModal("")
					}
					sizes={{ md: 3 }}
				/>
			</Grid2>
			<UpsertDeleteModal
				adjustmentType={selectedRowData}
				onHide={onModalHide}
				modal={showModal}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</Grid2>
	);
};
